import { createContext, useContext, useEffect, useState } from 'react';
import { authService } from '../../services/auth-service';
import { AuthUser, AuthSession } from '../../types/auth';
import { clearAllAuthData } from '../../utils/auth-utils';

interface AuthContextType {
  session: AuthSession | null;
  loading: boolean;
  error: string | null;
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<any>;
  forgotPassword: (email: string) => Promise<any>;
  googleAuth: () => Promise<any>;
  resetPassword: (password: string, token?: string) => Promise<any>;
  signUp: (email: string, password: string, metadata?: { 
    first_name?: string; 
    last_name?: string; 
    userType?: 'student' | 'teacher'; 
    classGrade?: string;
  }) => Promise<any>;
  refreshAccessToken: () => Promise<any>;
  logout: () => Promise<any>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  loading: true,
  error: null,
  user: null,
  login: () => Promise.resolve({ error: null }),
  forgotPassword: () => Promise.resolve({ error: null }),
  googleAuth: () => Promise.resolve({ error: null }),
  resetPassword: () => Promise.resolve({ error: null }),
  signUp: () => Promise.resolve({ error: null }),
  logout: () => Promise.resolve({ error: null }),
  refreshAccessToken: () => Promise.resolve({ error: null }),
});

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Utility function to safely set user data in localStorage
  const setUserInLocalStorage = (user: AuthUser | null) => {
    try {
      if (user && typeof user === 'object' && user.id) {
        localStorage.setItem('ace-auth', JSON.stringify(user));
      } else {
        localStorage.removeItem('ace-auth');
      }
    } catch (error) {
      console.error('Error setting user in localStorage:', error);
    }
  };

  // Utility function to completely clear all auth state
  const clearAllAuthState = () => {
    setUser(null);
    setSession(null);
    setError(null);
    clearAllAuthData(); // Use the comprehensive utility
    authService.clearStoredSession();
  };
  const [user, setUser] = useState<AuthUser | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Ensure localStorage is always synchronized with user state
  useEffect(() => {
    setUserInLocalStorage(user);
  }, [user]);

  // Initialize authentication state
  useEffect(() => {
    let isMounted = true;
    
    // Check if password was recently reset
    const passwordRecentlyReset = localStorage.getItem('password-recently-reset');
    if (passwordRecentlyReset) {
      clearAllAuthData();
      localStorage.removeItem('password-recently-reset');
      setInitialized(true);
      return;
    }
    
    // Try to load user from localStorage first
    try {
      const savedUser = localStorage.getItem('ace-auth');
      if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
        const userData = JSON.parse(savedUser);
        // Validate the user data before setting it
        if (userData && typeof userData === 'object' && userData.id) {
          setUser(userData);
        } else {
          // Invalid user data, clear it
          localStorage.removeItem('ace-auth');
        }
      }
    } catch (e) {
      console.error('Error loading auth from localStorage', e);
      // Clear invalid data
      localStorage.removeItem('ace-auth');
    }
    
    // Check for stored session from our auth service
    const storedSession = authService.getStoredSession();
    
    if (storedSession && isMounted) {
      const user: AuthUser = storedSession.user;
      
      const session: AuthSession = {
        access_token: storedSession.access_token,
        refresh_token: storedSession.refresh_token,
        expires_in: storedSession.expires_in,
        expires_at: storedSession.expires_at,
        token_type: storedSession.token_type,
        user: user
      };
      
      setSession(session);
      setUser(user);
      setLoading(false);
      setInitialized(true);
      return;
    }
    
    // No stored session found
    setLoading(false);
    setInitialized(true);

    return () => {
      isMounted = false;
    };
  }, []);

  // Listen for storage events and custom events to pick up OAuth callbacks
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'ace-auth-session' && e.newValue) {
        updateAuthFromData(e.newValue);
      }
    };

    const handleAuthSessionUpdate = (e: CustomEvent) => {
      if (e.detail?.authData) {
        updateAuthFromData(JSON.stringify(e.detail.authData));
      }
    };

    const handleForceAuthRefresh = () => {
      // Re-check stored session
      const storedSession = authService.getStoredSession();
      if (storedSession) {
        const user: AuthUser = storedSession.user;
        
        const session: AuthSession = {
          access_token: storedSession.access_token,
          refresh_token: storedSession.refresh_token,
          expires_in: storedSession.expires_in,
          expires_at: storedSession.expires_at,
          token_type: storedSession.token_type,
          user: user
        };
        
        setSession(session);
        setUser(user);
        setError(null);
      }
    };

    // Handle token refresh events from auth service
    const handleTokenRefreshed = (e: CustomEvent) => {
      if (e.detail?.session) {
        const newSession = e.detail.session;
        const user: AuthUser = newSession.user;
        
        const session: AuthSession = {
          access_token: newSession.access_token,
          refresh_token: newSession.refresh_token,
          expires_in: newSession.expires_in,
          expires_at: newSession.expires_at,
          token_type: newSession.token_type,
          user: user
        };
        
        setSession(session);
        setUser(user);
        setError(null);
      }
    };

    const updateAuthFromData = (dataString: string) => {
      try {
        const authData = JSON.parse(dataString);
        const user: AuthUser = authData.user;
        
        const session: AuthSession = {
          access_token: authData.access_token,
          refresh_token: authData.refresh_token,
          expires_in: authData.expires_in,
          expires_at: authData.expires_at,
          token_type: authData.token_type,
          user: user
        };
        
        setSession(session);
        setUser(user);
        setError(null);
      } catch (error) {
        // Handle error silently or provide user feedback
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-session-updated', handleAuthSessionUpdate as EventListener);
    window.addEventListener('force-auth-refresh', handleForceAuthRefresh);
    window.addEventListener('token-refreshed', handleTokenRefreshed as EventListener);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-session-updated', handleAuthSessionUpdate as EventListener);
      window.removeEventListener('force-auth-refresh', handleForceAuthRefresh);
      window.removeEventListener('token-refreshed', handleTokenRefreshed as EventListener);
    };
  }, []);

  // Set up automatic token refresh
  useEffect(() => {
    if (!user || !session) return;

    const refreshInterval = setInterval(async () => {
      try {
        const storedSession = authService.getStoredSession();
        if (!storedSession) {
          // Session was cleared externally, update local state
          setSession(null);
          setUser(null);
          localStorage.removeItem('ace-auth');
          return;
        }

        const now = Date.now();
        const expiryTime = storedSession.expires_at * 1000;
        // Refresh 5 minutes before expiry for seamless user experience
        const refreshThreshold = 5 * 60 * 1000; // 5 minutes

        if ((expiryTime - now) <= refreshThreshold) {
          const refreshResult = await authService.refreshToken();
          
          if (refreshResult.data && !refreshResult.error) {
            const newSession = refreshResult.data;
            
            const updatedUser: AuthUser = newSession.user;
            
            const updatedSession: AuthSession = {
              access_token: newSession.access_token,
              refresh_token: newSession.refresh_token,
              expires_in: newSession.expires_in,
              expires_at: newSession.expires_at,
              token_type: newSession.token_type,
              user: updatedUser
            };
            
            setSession(updatedSession);
            setUser(updatedUser);
            
            // Clear any previous errors since refresh was successful
            setError(null);
          } else {
            console.error('Automatic token refresh failed:', refreshResult.error);
            // Don't immediately log out on refresh failure during automatic refresh
            // Let the user continue until their session actually expires
            // The API calls will handle the 401 errors when they occur
          }
        }
      } catch (error) {
        console.error('Error during automatic token refresh:', error);
        // Don't immediately clear auth state during automatic refresh errors
        // Let the natural expiration and API error handling deal with it
      }
    }, 300000); // Check every 5 minutes

    return () => clearInterval(refreshInterval);
  }, [user?.id, session?.access_token]); // Only depend on user ID and access token to prevent unnecessary re-renders

  const value = {
    session,
    loading: loading && !initialized,
    error,
    user,
    login: async (email: string, password: string) => {
      try {
        const result = await authService.login(email, password);
        
        if (result.error) {
          setError(result.error.message);
          return { error: result.error };
        } else {
          setError(null);
          if (result.data?.user) {
            const user: AuthUser = result.data.user;
            
            setUser(user);
            
            const session: AuthSession = {
              access_token: result.data.access_token,
              refresh_token: result.data.refresh_token,
              expires_in: result.data.expires_in,
              expires_at: result.data.expires_at,
              token_type: result.data.token_type,
              user: user
            };
            
            setSession(session);
            setUserInLocalStorage(user);
            
            // Trigger profile fetch after successful login
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('auth-login-success', {
                detail: { user }
              }));
            }, 100);
          }
        }
        return result;
      } catch (err: any) {
        setError(err.message || 'Login failed');
        return { error: err };
      }
    },
    forgotPassword: async (email: string) => {
      try {
        const result = await authService.forgotPassword(email);
        
        if (result.error) {
          console.error('Forgot password error:', result.error);
          setError(result.error.message);
          return { error: result.error };
        } else {
          setError(null);
          
          // Clear any existing auth state since user is resetting password
          // This helps prevent auth state issues
          clearAllAuthState();
          
          return { data: result.data };
        }
      } catch (err: any) {
        console.error('Forgot password exception:', err);
        setError(err.message || 'Password reset failed');
        return { error: err };
      }
    },
    googleAuth: async () => {
      try {
        const redirectUrl = `${window.location.origin}/auth/callback`;
        
        const result = await authService.googleAuth(redirectUrl);
        
        if (result.error) {
          // If backend endpoint fails, show helpful error message
          if (result.error.code === '404') {
            setError('Backend Google OAuth endpoint not found. Please ensure your backend server is running.');
          } else {
            setError(result.error.message);
          }
          return { error: result.error };
        } else {
          setError(null);
          // Redirect user to Google OAuth URL
          if (result.data?.auth_url) {
            // Force immediate redirect to Google OAuth
            window.location.href = result.data.auth_url;
          } else {
            const errorMsg = 'No auth URL received from backend';
            setError(errorMsg);
            return { error: { message: errorMsg } };
          }
          return { data: result.data };
        }
      } catch (err: any) {
        setError(err.message || 'Google authentication failed');
        return { error: err };
      }
    },
    resetPassword: async (password: string, token?: string) => {
      try {
        // Get token from URL if not provided
        const resetToken = token || new URLSearchParams(window.location.search).get('token');
        
        if (!resetToken) {
          setError('Reset token not found');
          return { error: { message: 'Reset token not found' } };
        }
        
        // Clear any existing auth state before resetting password
        clearAllAuthState();
        
        const result = await authService.resetPassword(password, resetToken);
        
        if (result.error) {
          setError(result.error.message);
          return { error: result.error };
        } else {
          setError(null);
          
          // Don't automatically log in after password reset
          // User should manually log in with new password for security
          
          // Set a flag to indicate password was recently reset
          localStorage.setItem('password-recently-reset', 'true');
          
          return { data: { success: true, message: 'Password reset successful' } };
        }
      } catch (err: any) {
        setError(err.message || 'Password update failed');
        return { error: err };
      }
    },
    signUp: async (email: string, password: string, metadata?: { 
      first_name?: string; 
      last_name?: string; 
      userType?: 'student' | 'teacher'; 
      classGrade?: string;
    }) => {
      try {
        const apiMetadata = {
          first_name: metadata?.first_name || '',
          last_name: metadata?.last_name || '',
          user_type: metadata?.userType || 'student',
          class: metadata?.classGrade
        };
        
        const result = await authService.signup(email, password, apiMetadata);
        
        if (result.error) {
          setError(result.error.message);
          return { error: result.error };
        } else {
          setError(null);
          return { 
            data: result.data, 
            requiresLogin: true,
            message: 'Account created successfully. Please log in with your credentials.'
          };
        }
      } catch (err: any) {
        setError(err.message || 'Sign up failed');
        return { error: err };
      }
    },
    logout: async () => {
      try {
        const currentUserId = user?.id;
        
        const result = await authService.logout();
        
        if (result.error) {
          setError(result.error.message);
        } else {
          setError(null);
          
          // Clear ALL auth and session state using the comprehensive utility
          clearAllAuthState();
          
          // Explicitly clear the ace-auth key to ensure it's removed
          localStorage.removeItem('ace-auth');
          localStorage.removeItem('ace-auth-session');
          
          // Clear any remaining user-specific keys (belt and suspenders approach)
          if (currentUserId) {
            // Clear from localStorage
            localStorage.removeItem(`assessment_check_${currentUserId}`);
            localStorage.removeItem(`assessment_session_${currentUserId}`);
            localStorage.removeItem(`profile_completion_dismissed_${currentUserId}`);
           
            localStorage.removeItem(`assessment_completed_${currentUserId}`);
            
            
          }
          
          // Also clear common session keys that don't have user ID
          sessionStorage.removeItem('profile_data');
          sessionStorage.removeItem('profile_cache_timestamp');
          sessionStorage.removeItem('profile_strength');
          
          // Dispatch custom event to notify other contexts/components of logout
          window.dispatchEvent(new CustomEvent('user-logout', {
            detail: { userId: currentUserId }
          }));
        }
        
        return result;
      } catch (err: any) {
        setError(err.message || 'Logout failed');
        return { error: err };
      }
    },
    refreshAccessToken: async () => {
      try {
        const refreshResult = await authService.refreshToken();
        
        if (refreshResult.data && !refreshResult.error) {
          const newSession = refreshResult.data;
          
          const updatedUser: AuthUser = {
            id: newSession.user.id,
            aud: newSession.user.aud,
            role: newSession.user.role,
            email: newSession.user.email,
            email_confirmed_at: newSession.user.email_confirmed_at,
            phone: newSession.user.phone,
            confirmed_at: newSession.user.confirmed_at,
            last_sign_in_at: newSession.user.last_sign_in_at,
            app_metadata: newSession.user.app_metadata,
            user_metadata: newSession.user.user_metadata,
            identities: newSession.user.identities,
            created_at: newSession.user.created_at,
            updated_at: newSession.user.updated_at,
            is_anonymous: newSession.user.is_anonymous
          };
          
          const updatedSession: AuthSession = {
            access_token: newSession.access_token,
            refresh_token: newSession.refresh_token,
            expires_in: newSession.expires_in,
            expires_at: newSession.expires_at,
            token_type: newSession.token_type,
            user: updatedUser
          };
          
          setSession(updatedSession);
          setUser(updatedUser);
          setUserInLocalStorage(updatedUser);
          setError(null); // Clear any previous errors
          
          return { data: refreshResult.data, error: null };
        } else {
          const errorMsg = refreshResult.error?.message || 'Token refresh failed';
          console.error('Manual token refresh failed:', errorMsg);
          setError(errorMsg);
          
          // If manual refresh fails, clear auth state since user explicitly requested refresh
          clearAllAuthState();
          
          return { data: null, error: refreshResult.error };
        }
      } catch (error: any) {
        const errorMsg = error.message || 'Token refresh failed';
        console.error('Manual token refresh error:', errorMsg);
        setError(errorMsg);
        
        // Clear auth state on manual refresh failure
        clearAllAuthState();
        
        return { data: null, error: { message: errorMsg } };
      }
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {(!loading || initialized) && children}
    </AuthContext.Provider>
  );
}